import React, { useState, useEffect } from 'react';
import { Placeholder } from 'react-bootstrap';
import {
  Wallet, TrendingUp, ArrowDownCircle, Percent, Info,
  Download, Plus, Landmark, FileText, HandCoins, ArrowRight,
  Calendar, Briefcase, Coins
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell
} from 'recharts';

import layoutStyles from '../Dashboard2/Dashboard2.module.css';
import savingsStyles from './Savings.module.css';

import Sidebar2 from '../../Components/SideBar2/SideBar2';
import Header2 from '../../Components/Header2/Header2';
import axios from 'axios';
import { BASE_URL } from '../../Components/Api/Api';
import { useNavigate } from "react-router-dom";

// ---------------------------------------------------------------------------
// Static config — only labels/icons/keys live here. Actual numbers always
// come from the `stats` / `summary` / `growth` / `breakDown` state.
// ---------------------------------------------------------------------------

const completeGrowthData = (apiData = []) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return months.map((month) => {
    const item = apiData.find(
      (g) => g.month?.toLowerCase() === month.toLowerCase()
    );

    return {
      month,
      amount: Number(item?.amount) || 0,
    };
  });
};

const cardConfig = [
  { id: 1, key: 'total_savings', label: 'Current Balance', sub: 'Total Available Balance', icon: Wallet, iconStyle: savingsStyles.iconGreen },
  { id: 2, key: 'month_savings', label: 'This Month Savings', sub: '', icon: Calendar, iconStyle: savingsStyles.iconGreen, showTrend: true },
  { id: 4, key: 'total_withdrawals', label: 'Total Withdrawals', sub: 'All time withdrawals', icon: Coins, iconStyle: savingsStyles.iconOrange },
  { id: 5, key: 'interest_earned', label: 'Interest Earned', sub: 'Year to date', icon: Percent, iconStyle: savingsStyles.iconPurple },
];

const summaryConfig = [
  { label: 'Current Balance', key: 'available_balance' },
  { label: 'This Month Savings', key: 'month_savings' },
  { label: 'Total Deposits', key: 'total_deposits' },
  { label: 'Total Withdrawals', key: 'total_withdrawals' },
  { label: 'Interest Earned', key: 'interest_earned' },
];

const BREAKDOWN_COLORS = ['#15803d', '#2563eb', '#ea580c', '#7c3aed', '#dc2626', '#0891b2', '#e2e8f0'];

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value) => `₦${Number(value || 0).toLocaleString('en-NG')}`;
const formatCurrencys = (value) =>
  Number(value ?? 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  
const formatMetricValue = (value, type = "number") => {
  const numericValue = Number(value);

  // Handle invalid values
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(numericValue)
  ) {
    return type === "currency" ? "₦0.00" : "0";
  }

  // Percent
  if (type === "percent") {
    return `${numericValue.toFixed(0)}%`;
  }

  // Currency
  if (type === "currency") {
    const absValue = Math.abs(numericValue);
    const sign = numericValue < 0 ? "-" : "";

    let formatted;

    if (absValue >= 1_000_000_000_000) {
      formatted = `${(absValue / 1_000_000_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}T`;
    } else if (absValue >= 1_000_000_000) {
      formatted = `${(absValue / 1_000_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}B`;
    } else if (absValue >= 1_000_000) {
      formatted = `${(absValue / 1_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}M`;
    } else if (absValue >= 1_000) {
      formatted = `${(absValue / 1_000)
        .toFixed(1)
        .replace(/\.0$/, "")}K`;
    } else {
      formatted = absValue.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return `${sign}₦${formatted}`;
  }

  // Normal numbers
  if (Number.isInteger(numericValue)) {
    return numericValue.toLocaleString("en-NG");
  }

  return numericValue.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};


const formatYAxis = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
  return value;
};

const formatPointLabel = (value) => {
  const amount = Number(value) || 0;

  if (amount === 0) return "0.00";

  if (amount >= 1000000) {
    return `₦${(amount / 1000000).toFixed(1)}M`;
  }

  if (amount >= 1000) {
    return `₦${(amount / 1000).toFixed(0)}K`;
  }

  return `₦${amount}`;
};

// ---------------------------------------------------------------------------
// Reusable "empty state" block — centers a message inside a card/section
// ---------------------------------------------------------------------------

const NoData = ({ message = 'No data available', height = '100%' }) => (
  <div
    style={{
      height,
      minHeight: 160,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#94a3b8',
      fontSize: '0.875rem',
      fontWeight: 500,
      textAlign: 'center',
      width: '100%',
    }}
  >
    {message}
  </div>
);

// ---------------------------------------------------------------------------
// react-bootstrap Placeholder skeletons — one per section
// ---------------------------------------------------------------------------

const CardSkeleton = () => (
  <div className={savingsStyles.statCard}>
    {/* <div className={savingsStyles.statLabelRow}>
      <Placeholder as="p" animation="glow" style={{ width: '60%', margin: 0 }}>
        <Placeholder xs={8} />
      </Placeholder>
    </div> */}
    <div className={savingsStyles.statValueRow}>
      <div className={`${savingsStyles.statIconWrapper}`} style={{ opacity: 0.4 }}>
        <Placeholder as="span" animation="glow">
          <Placeholder xs={12} style={{ width: 40, height: 40, borderRadius: '50%', display: 'inline-block' }} />
        </Placeholder>
      </div>
      <Placeholder as="h4" animation="glow" style={{ width: '70%', margin: "10px 0 0 0" }}>
        <Placeholder xs={10} size="lg" />
      </Placeholder>
    </div>
    <Placeholder as="p" animation="glow" style={{ width: '50%', marginTop: '0.5rem' }}>
      <Placeholder xs={6} />
    </Placeholder>
    <div className={savingsStyles.statLabelRow}>
      <Placeholder as="p" animation="glow" style={{ width: '60%', margin: 0 }}>
        <Placeholder xs={8} />
      </Placeholder>
    </div>
  </div>
);

const LineChartSkeleton = () => (
  <div className={savingsStyles.card}>
    <div className={savingsStyles.cardHeader}>
      <Placeholder as="h3" animation="glow" style={{ width: '40%', margin: 0 }}>
        <Placeholder xs={8} />
      </Placeholder>
    </div>
    <div className={savingsStyles.chartWrapper}>
      <Placeholder as="div" animation="wave" style={{ width: '100%', height: '100%' }}>
        <Placeholder xs={12} style={{ width: '100%', height: '100%', minHeight: 220 }} />
      </Placeholder>
    </div>
  </div>
);

const PieChartSkeleton = () => (
  <div className={savingsStyles.card}>
    <div className={savingsStyles.cardHeader}>
      <Placeholder as="h3" animation="glow" style={{ width: '50%', margin: 0 }}>
        <Placeholder xs={8} />
      </Placeholder>
    </div>
    <div className={savingsStyles.breakdownRow}>
      <div className={savingsStyles.breakdownPieWrapper} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Placeholder as="div" animation="wave">
          <Placeholder xs={12} style={{ width: 150, height: 150, borderRadius: '50%', display: 'inline-block' }} />
        </Placeholder>
      </div>
      <div className={savingsStyles.breakdownLegend}>
        {[1, 2, 3].map((i) => (
          <Placeholder as="p" animation="glow" key={i} style={{ width: '80%' }}>
            <Placeholder xs={10} />
          </Placeholder>
        ))}
      </div>
    </div>
  </div>
);

const SummarySkeleton = () => (
  <div className={savingsStyles.card}>
    <Placeholder as="h3" animation="glow" style={{ width: '40%' }}>
      <Placeholder xs={8} />
    </Placeholder>
    <div className={savingsStyles.summaryTable}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={savingsStyles.summaryRow}>
          <Placeholder as="span" animation="glow" style={{ width: '45%' }}>
            <Placeholder xs={8} />
          </Placeholder>
          <Placeholder as="span" animation="glow" style={{ width: '30%' }}>
            <Placeholder xs={8} />
          </Placeholder>
        </div>
      ))}
    </div>
  </div>
);

const TableSkeleton = () => (
  <div className={savingsStyles.card}>
    <div className={savingsStyles.cardHeader} style={{ marginBottom: '0.5rem' }}>
      <Placeholder as="h3" animation="glow" style={{ width: '40%', margin: 0 }}>
        <Placeholder xs={8} />
      </Placeholder>
    </div>
    <div className={savingsStyles.tableWrapper}>
      <table className={savingsStyles.transactionTable}>
        <thead>
          {/* <tr> */}
           <tr>
                            <th>S/N</th>
                            <th>Description</th>
                            {/* <th>Type</th> */}
                            <th>Debit</th>
                            <th>Credit</th>
                            <th>Balance</th>
                          {/* </tr> */}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              {[1, 2, 3, 4, 5].map((c) => (
                <td key={c}>
                  <Placeholder as="span" animation="glow" style={{ width: '80%', display: 'inline-block' }}>
                    <Placeholder xs={8} />
                  </Placeholder>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function Savings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [summary, setSummary] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [growth, setGrowth] = useState([]);
    const [ledgers, setLedgers] = useState([]);
  const [breakDown, setBreakDown] = useState([]);
  

  const fetchMetric = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const companyid = localStorage.getItem('companyId');
    const compid = localStorage.getItem("companyId");
    const memberid = localStorage.getItem("memberId");

      const res = await axios.get(
        `${BASE_URL}/member_stat_profile?company_id=${compid}&member_id=${memberid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      const metric = res.data?.data || {};

      setStats(metric.summary || {});
      setSummary(metric.summary || {});
      setTableData(metric.recent_transactions || []);
      // setGrowth(metric.growth || []);
      setGrowth(completeGrowthData(metric.growth || []));
      setBreakDown(metric.savings_breakdown || []);
    } catch (error) {
      console.error(error);
      setStats({});
      setSummary({});
      setTableData([]);
      setGrowth([]);
      setBreakDown([]);
    } finally {
      setLoading(false);
    }
  };

    const fetchLedger = async () => {
      setLoading(true);
  
      try {
        const token = localStorage.getItem("token");
        const companyid = localStorage.getItem("companyId");
        const compid = localStorage.getItem("companyId");
        const memberid = localStorage.getItem("memberId");
  
        let url = `${BASE_URL}/my_savings_ledger?company_id=${companyid}&member_id=${memberid}`;
  
        // if (savingsId) {
        //   url += `&savings_id=${savingsId}`;
        // }
        // if (date) {
        //   url += `&date=${date}`;
        // }
  
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
  
        const data = res.data?.data || {};
        setLedgers(data.table_data || []);
        // setStats(data.summary || {});
        // setCurrentPage(1); // reset to page 1 whenever the underlying dataset changes
      } catch (error) {
        console.error(error);
        setLedgers([]);
        setStats({});
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
    fetchMetric();
    fetchLedger();
   }, []);

  // ---- derived flags ----
  const tableHasData = Array.isArray(ledgers) && ledgers.length > 0;
  // Show only the latest 5 ledger records
const recentLedgers = [...ledgers]
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  .slice(0, 5);

/*
const getFirstFiveLedgers = () => {
  return [...ledgers]
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .slice(0, 5);
};
*/
  const summaryHasData = summary && Object.keys(summary).length > 0;
  const growthHasData = Array.isArray(growth) && growth.length > 0;
  const breakDownHasData = Array.isArray(breakDown) && breakDown.length > 0;

  const totalSavingsForPie = stats?.total_savings ?? 0;

  return (
    <div className={layoutStyles.container}>
      <Sidebar2 />
      <main className={layoutStyles.mainContent}>
        <Header2 />

        <div className={layoutStyles.workspace}>
          <div className={savingsStyles.savingsPage}>
            {/* 1. STAT CARDS */}
            <div className={savingsStyles.statGrid}>
              {loading
                ? cardConfig.map((item) => <CardSkeleton key={item.id} />)
                : cardConfig.map((item) => {
                    const value = stats?.[item.key] ?? 0;
                    return (
                      <div key={item.id} className={savingsStyles.statCard}>
                     
                        <div 
                        className={savingsStyles.statLabelRow}>
                          <div className={`${savingsStyles.statIconWrapper} ${item.iconStyle}`}>
                            <item.icon size={24} />
                          </div>
                        </div>

                          <div className={savingsStyles.statValueRow}>
                          <p className={savingsStyles.statLabel}>{item.label}</p>
                           <h4 className={savingsStyles.statValue}>
                            {item.key === "interest_earned"
                              ? "0.00"
                              : formatMetricValue(stats?.[item.key], "currency")
                              }
                              {/* `₦${formatMetricValue(stats.total_loan_owe)}` */}
                              {/* formatCurrency(stats?.[item.key]) */}
                          </h4>
                          {/* <Info size={16} className={savingsStyles.infoIcon} /> */}
                        </div>

                        {item.sub && <p className={savingsStyles.statSub}>{item.sub}</p>}
                        {item.showTrend && (
                          <p className={`${savingsStyles.statSub} ${savingsStyles.trendUp}`}>
                            {/* This month */}
                            <TrendingUp size={14} /> This month savings
                          </p>
                        )}
                      </div>
                    );
                  })}
            </div>

            {/* 2. CHARTS GRID */}
            <div className={savingsStyles.chartGrid}>
              {/* Savings Growth */}
              {loading ? (
                <LineChartSkeleton />
              ) : (
                <div className={savingsStyles.card}>
                  <div className={savingsStyles.cardHeader}>
                    <h3 className={savingsStyles.cardTitle}>Savings Growth (This Year)</h3>
                    {/* <select className={savingsStyles.selectDropdown}>
                      <option>This Year</option>
                    </select> */}
                  </div>
                  <div className={savingsStyles.chartWrapper}>
                    {!growthHasData ? (
                      <NoData message="No growth data available" />
                    ) : growth.length === 1 ? (
                      // Single data point — an area/line chart has nothing to connect,
                      // so fall back to a single bar for a meaningful visual.
                     <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={growth}
                        margin={{ top: 25, right: 25, left: 15, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorGrowthScoping" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#15803d" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#15803d" stopOpacity={0} />
                          </linearGradient>
                        </defs>

                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#f1f5f9"
                        />

                        {/* <XAxis
                          dataKey="month"
                          tick={{ fontSize: 10, fill: "#94a3b8" }}
                          axisLine={false}
                          tickLine={false}
                        /> */}

                          <XAxis
                        dataKey="month"
                        tick={{ fontSize: 10, fill: "#94a3b8" }}
                        axisLine={false}
                        tickLine={false}
                      />

                        {/* <YAxis
                          width={45}
                          tickFormatter={formatYAxis}
                          tick={{ fontSize: 10, fill: "#94a3b8" }}
                          axisLine={false}
                          tickLine={false}
                        /> */}
                         <YAxis hide />
                          
                        <Tooltip
                          formatter={(value) => `N${Number(value).toLocaleString()}`}
                        />

                        <Area
                          type="monotone"
                          dataKey="amount"
                          stroke="#15803d"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorGrowthScoping)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                    ) : (
                     <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={growth}
                      margin={{ top: 25, right: 25, left: 15, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorGrowthScoping" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#15803d" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#15803d" stopOpacity={0} />
                        </linearGradient>
                      </defs>

                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f1f5f9"
                      />

                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 10, fill: "#94a3b8" }}
                        axisLine={false}
                        tickLine={false}
                      />

                      <YAxis hide />

                      <Tooltip
                        formatter={(value) => `₦${Number(value).toLocaleString()}`}
                      />

                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="#15803d"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorGrowthScoping)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                    )}
                  </div>
                  {/* <a href="#" className={savingsStyles.viewLink}>
                    <span>View Full Report</span> <ArrowRight size={14} />
                  </a> */}
                </div>
              )}

              {/* Savings Breakdown */}
              {loading ? (
                <PieChartSkeleton />
              ) : (
                <div className={savingsStyles.card}>
                  <div className={savingsStyles.cardHeader}>
                    <h3 className={savingsStyles.cardTitle}>Savings Breakdown</h3>
                  </div>

                  {!breakDownHasData ? (
                    <NoData message="No breakdown data available" height={220} />
                  ) : (
                    <div className={savingsStyles.breakdownRow}>
                      <div className={savingsStyles.breakdownPieWrapper}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                            <Pie
                              data={breakDown}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={75}
                              paddingAngle={breakDown.length > 1 ? 3 : 0}
                              dataKey="percentage"
                              nameKey="name"
                            >
                              {breakDown.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value}%`} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className={savingsStyles.pieCenterText}>
                          <h4>{formatCurrency(totalSavingsForPie)}</h4>
                          <p>Total Savings</p>
                        </div>
                      </div>

                      <div className={savingsStyles.breakdownLegend}>
                        {breakDown.map((item, idx) => (
                          <div key={item.id ?? idx} className={savingsStyles.breakdownLegendItem}>
                            <span
                              className={savingsStyles.legendDot}
                              style={{ backgroundColor: BREAKDOWN_COLORS[idx % BREAKDOWN_COLORS.length] }}
                            ></span>
                            <div className={savingsStyles.legendTextContent}>
                              <span className={savingsStyles.legendTitle}>{item.name}</span>
                              <span className={savingsStyles.legendSub}>
                                {formatCurrency(item.amount)}{' '}
                                <span className={savingsStyles.legendPercent}>({item.percentage}%)</span>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* <a href="#" className={savingsStyles.viewLink}>
                    <span>View Breakdown</span> <ArrowRight size={14} />
                  </a> */}
                </div>
              )}
            </div>

            {/* 3. ACCOUNT SUMMARY & TRANSACTIONS */}
            <div className={savingsStyles.infoGrid}>
              {loading ? (
                <SummarySkeleton />
              ) : (
                <div className={savingsStyles.card}>
                  <h3 className={savingsStyles.cardTitle} style={{ marginBottom: '1rem' }}>Account Summary</h3>
                  {!summaryHasData ? (
                    <NoData message="No account summary available" height={200} />
                  ) : (
                    <div className={savingsStyles.summaryTable}>
                      {summaryConfig.map((row, idx) => (
                        <div key={idx} className={savingsStyles.summaryRow}>
                          <span className={savingsStyles.summaryLabel}>{row.label}</span>
                          <span className={savingsStyles.summaryValue}>
                          {row.label === "Interest Earned"
                            ? "0.00"
                            : formatCurrency(summary?.[row.key])}
                        </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* <button className={savingsStyles.downloadBtn}>
                    <span>Download Statement</span>
                    <Download size={16} />
                  </button> */}
                </div>
              )}

              {loading ? (
                <TableSkeleton />
              ) : (
                <div className={savingsStyles.card}>
                  <div className={savingsStyles.cardHeader} style={{ marginBottom: '0.5rem' }}>
                    <h3 className={savingsStyles.cardTitle}>Recent Transactions</h3>
                    <p  className={savingsStyles.viewLink} style={{ marginTop: 0 }}
                     onClick={() => navigate("/savings/ledger")}
                    >
                    View All</p>
                  </div>
                  {!tableHasData ? (
                    <NoData message="No transactions available" height={200} />
                  ) : (
                    <div className={savingsStyles.tableWrapper}>
                      <table className={savingsStyles.transactionTable}>
                        <thead>
                          <tr>
                            <th>S/N</th>
                            <th>Description</th>
                            {/* <th>Type</th> */}
                            <th style={{ textAlign: 'right' }}>Debit</th>
                            <th style={{ textAlign: 'right' }}>Credit</th>
                            <th style={{ textAlign: 'right' }}>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentLedgers.map((tx, idx) => (
                            <tr key={tx.id ?? idx}>
                              <td 
                              // style={{ color: '#64748b' }}
                              >{ idx + 1}</td>
                              <td>{tx.description}</td>
                              {/* <td>
                                <span
                                  className={`${savingsStyles.typeBadge} ${
                                    tx.type === 'Deposit'
                                      ? savingsStyles.badgeDeposit
                                      : tx.type === 'Withdrawal'
                                      ? savingsStyles.badgeWithdrawal
                                      : savingsStyles.badgeInterest
                                  }`}
                                >
                                  {tx.type}
                                </span>
                              </td> */}
                              <td style={{ textAlign: 'right' }}>{formatCurrencys(tx.debit)}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrencys(tx.credit)}</td>
                              <td style={{ textAlign: 'right' }}>
                                {formatCurrencys(tx.balance)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
